import { useState, useEffect, useCallback } from 'react';

const SESSION_KEY = 'embedded_sso_launch_time';

const MAX_DELAY_MS = 15 * 60 * 1000; // 15-minute safety cap

/**
 * Delays "Save to EHR" for a configurable period after SSO launch so the OP
 * data pipeline has time to make the composition resource available.
 *
 * Disabled by default. To enable, set a positive `ehr_save_delay_ms` value in
 * the org's EHR settings. Setting it to 0 or leaving it unset keeps the
 * feature off, which is the desired state for production until pipeline
 * performance improves.
 *
 * @param delayMs - Delay in milliseconds, sourced from ehrSettings.ehr_save_delay_ms.
 *                  Clamped to MAX_DELAY_MS to prevent misconfiguration from
 *                  blocking clinical workflows.
 */
export const useEhrSaveDelay = (delayMs?: number | null) => {
  const effectiveDelay = delayMs ? Math.min(delayMs, MAX_DELAY_MS) : 0;

  const getLaunchTime = useCallback(
    () => Number(sessionStorage.getItem(SESSION_KEY)) || 0,
    []
  );

  const calcRemaining = useCallback(() => {
    const launchTime = getLaunchTime();
    if (!launchTime) return 0;
    return Math.max(0, launchTime + effectiveDelay - Date.now());
  }, [effectiveDelay, getLaunchTime]);

  const [remainingMs, setRemainingMs] = useState(calcRemaining);

  useEffect(() => {
    setRemainingMs(calcRemaining());

    if (calcRemaining() <= 0) return;

    const interval = setInterval(() => {
      const remaining = calcRemaining();
      setRemainingMs(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [calcRemaining]);

  const isLocked = remainingMs > 0;
  const remainingMinutes = Math.ceil(remainingMs / 60_000);

  return { isLocked, remainingMs, remainingMinutes };
};
